const __RELATIVE_SOURCE_FILE__ /* __INJECTED__ */ = 'packages/patapp_web/src/config/usz/eCheckin/sections/Versicherung.tsx';
import { forwardRef } from 'react';
import { Divider } from '@mui/material';
import _ from 'lodash';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { CIS4mBlock } from '@cistec/common_web/modules/CIS4m/components/CIS4mBlock';
import { CIS4mDatePicker } from '@cistec/common_web/modules/CIS4m/components/CIS4mDatePicker';
import { CIS4mMaskedTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mMaskedTextField';
import { CIS4mSingleCheckbox } from '@cistec/common_web/modules/CIS4m/components/CIS4mSingleCheckbox';
import { CIS4mTextField } from '@cistec/common_web/modules/CIS4m/components/CIS4mTextField';
import { useCIS4m, useCIS4mData } from '@cistec/common_web/modules/CIS4m/hooks';
import { useText } from '@cistec/common_web/utils/intlUtils';
import ECheckinSection from 'App/components/eCheckin/components/ECheckinSection';
import { AddressBlock, AddressFieldsConfig } from 'App/components/eCheckin/components/fields/AddressBlock';
import { PhoneNumberField } from 'App/components/eCheckin/components/fields/PhoneNumberField';
import { YesNoField } from 'App/components/eCheckin/components/fields/YesNoField';
import { ECheckinSectionProps } from 'App/components/eCheckin/types';
import { generatePaths } from 'App/components/eCheckin/utils';
import textMapCommon from '../../../common/translations';
import textMap from '../translations';
import { EintrittsgrundField } from './fields/EintrittsgrundField';
import { KostentraegerField } from './fields/KostentraegerField';
import { VersicherungsklasseField } from './fields/VersicherungsklasseField';

const addressFieldsConfig: AddressFieldsConfig = {
    additionalInfo: {
        included: false,
    },
    country: {
        included: false,
    },
};

export const Versicherung = forwardRef((props: ECheckinSectionProps, ref: any) => {
    const { path } = props;
    const title = useText(props.title);

    const eCheckinSectionRef = useCIS4m(ref);
    const formData = useCIS4mData();
    const { formatMessage } = useIntl();

    const PATHS = generatePaths(path, [
        'reason',
        'accidentDate',
        'accidentNumber',
        'accidentNoNumber',
        'costUnit',
        'insurance',
        'insurancePolicyNo',
        'insuranceCardNo',
        'additionalInsurance',
        'additionalInsuranceFlex',
        'additionalInsuranceName',
        'additionalInsuranceNo',
        'company',
        'telephone',
        'class',
        'additionalAmbulant',
        'additionalAmbulantName',
    ] as const);

    const costUnit = _.get(formData, PATHS.costUnit);

    const isAccident = _.get(formData, PATHS.reason) === 'accident';
    const isSelfPayer = ['selfPayer', undefined].includes(costUnit);
    const isAccidentInsurance = costUnit === 'accidentInsurance';
    const hasAdditionalInsurance = _.get(formData, PATHS.additionalInsurance) === '1';
    const hasAdditionalAmbulantInsurance = _.get(formData, PATHS.additionalAmbulant) === '1';
    const showUpgradeInfo = ['generalWard', 'semiPrivate'].includes(_.get(formData, PATHS.class));

    return (
        <ECheckinSection ref={eCheckinSectionRef} path={path} title={title}>
            <h1>{title}</h1>
            <EintrittsgrundField label={textMap.behandlungsgrund} path={PATHS.reason} required />

            <CIS4mBlock hidden={!isAccident}>
                <CIS4mDatePicker label={textMap.accidentDate} path={PATHS.accidentDate} maxDate={moment()} required />
                <CIS4mTextField label={textMap.accidentNumber} path={PATHS.accidentNumber} />
                <CIS4mSingleCheckbox label={textMap.accidentNoNumber} path={PATHS.accidentNoNumber} />
                <Divider />
            </CIS4mBlock>

            <KostentraegerField label={textMap.accidentCostUnit} path={PATHS.costUnit} required />
            <CIS4mBlock hidden={isSelfPayer}>
                <CIS4mTextField label={textMap.insuranceName} path={PATHS.insurance} required />
                <CIS4mTextField label={textMap.insurancePolicyNo} path={PATHS.insurancePolicyNo} />
                <CIS4mMaskedTextField
                    label={textMap.insuranceCardNo}
                    path={PATHS.insuranceCardNo}
                    required
                    mask={'80999999999999999999'}
                />
                <Divider />
            </CIS4mBlock>

            <YesNoField
                label={textMap.additionalInsurance}
                path={PATHS.additionalInsurance}
                required
                hidden={isSelfPayer}
            />
            <CIS4mBlock hidden={!hasAdditionalInsurance || isSelfPayer}>
                <YesNoField label={textMap.additionalInsuranceFlex} path={PATHS.additionalInsuranceFlex} required />
                <CIS4mTextField label={textMap.additionalInsuranceName} path={PATHS.additionalInsuranceName} required />
                <CIS4mTextField label={textMap.additionalInsuranceNo} path={PATHS.additionalInsuranceNo} />
                <Divider />
            </CIS4mBlock>

            <CIS4mBlock hidden={!isAccidentInsurance}>
                <CIS4mTextField label={textMap.company} path={PATHS.company} />
                <AddressBlock basePath={path} fieldsConfig={addressFieldsConfig} />
                <PhoneNumberField label={textMapCommon.tel} path={PATHS.telephone} />
                <Divider />
            </CIS4mBlock>

            {isAccidentInsurance || hasAdditionalInsurance || (!isSelfPayer && <Divider />)}

            <VersicherungsklasseField label={textMap.class} path={PATHS.class} required />
            <CIS4mBlock hidden={!showUpgradeInfo}>
                <div dangerouslySetInnerHTML={{ __html: formatMessage(textMap.insuranceText) }} />
                <Divider />
            </CIS4mBlock>
            <YesNoField label={textMap.additionalAmbulant} path={PATHS.additionalAmbulant} required />
            <CIS4mTextField
                label={textMap.additionalAmbulantName}
                path={PATHS.additionalAmbulantName}
                required
                hidden={!hasAdditionalAmbulantInsurance}
            />
        </ECheckinSection>
    );
});

Versicherung.displayName = 'Versicherung';

export default Versicherung;
